import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { finalize, catchError, throwError } from 'rxjs';
import {
  LoadingShowAction,
  LoadingHiddeAction,
} from '../../store/loading/loading.actions';

// Contador de peticiones activas
let activeRequests = 0;

/**
 * Interceptor que maneja una cola de peticiones HTTP y controla el estado de loading
 * Muestra el loading cuando hay peticiones activas y lo oculta cuando todas terminan
 */
export const loadingQueueInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const store = inject(Store);

  // Verificar si la petición debe ser excluida del loading
  if (shouldExcludeFromLoading(req)) {
    return next(req);
  }

  // Incrementar contador y mostrar loading si es la primera petición
  activeRequests++;
  if (activeRequests === 1) {
    store.dispatch(new LoadingShowAction());
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Manejar errores sin afectar el conteo
      return throwError(() => error);
    }),
    finalize(() => {
      // Decrementar contador y ocultar loading si no hay más peticiones
      activeRequests--;
      if (activeRequests <= 0) {
        activeRequests = 0; // Asegurar que nunca sea negativo
        if (!shouldExcludeFromLoading(req)) {
          store.dispatch(new LoadingHiddeAction());
        }
      }
    })
  );
};

/**
 * Determina si una petición debe ser excluida del sistema de loading
 * @param req La petición HTTP a evaluar
 * @returns true si debe ser excluida, false en caso contrario
 */
function shouldExcludeFromLoading(req: HttpRequest<unknown>): boolean {
  // URLs que no deben mostrar loading (ej: refresh token, polling, etc.)
  const excludedUrls = [
    '/refresh',
    'prediccion/seasonal/edit',
    'prediccion/standard/temporal',
    'prediccion/standard/edit',
    'prediccion/standard/temporal',
    'prediccion/standard/generate',
    'holyweek/generate',
    'recessweek/generate',
    'endyear/generate',
    'vacation/generate',
    'holyweek/edit',
    'recessweek/edit',
    'endyear/edit',
    'vacation/edit',
    'administracion/clientes/filters/clientes',
    'administracion/clientes/filters/city-gates',
    'prediccion/calendario/prediccion/standard?fechaInicio',
  ];

  // Headers que indican que no se debe mostrar loading
  const excludeLoadingHeader = req.headers.get('X-Skip-Loading');
  if (excludeLoadingHeader === 'true') {
    return true;
  }

  // Verificar URLs excluidas
  return excludedUrls.some(url => req.url.includes(url));
}

/**
 * Función utilitaria para resetear el contador de peticiones activas
 * Útil en casos de error crítico o reinicio de aplicación
 */
export function resetLoadingQueue(): void {
  activeRequests = 0;
}
